import React, { useEffect, useState } from "react";
import {
  FIELD_REQUIRED,
  IDENTITIES_TIK_TOK_EMPTY,
  LOADING,
} from "../../i18n/keysTranslations";
import i18next from "i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { Controller, useController } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startGetIdentitiesTikTokBusiness } from "../../actions/adsCampaigns";
import {
  ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
} from "../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import FieldCaption from "./FieldCaption";

const filter = createFilterOptions();

const SelectTikTokIdentityCustom = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  objective = ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  integrationID,
  adAccountID,
  sxTextField = {},
  type = "",
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const error = getReactHookFormNestedError(errors, name);

  const {
    field: { onChange: fieldOnChange, value: fieldValue },
  } = useController({
    name,
    control,
  });

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const isAppSales =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
    type === ADS_CAMPAIGN_TYPE_APP_OPTION;

  const getInitialData = async () => {
    if (!integrationID || !adAccountID) return;

    setLoading(true);

    const response = await dispatch(
      startGetIdentitiesTikTokBusiness({
        objective,
        adAccountID,
        integrationID,
      })
    );
    if (response.ok) {
      const newOptions = response.data || [];
      const filteredOptions = isAppSales
        ? newOptions
        : newOptions.filter((option) => option.type !== "CUSTOMIZED_USER");
      setOptions(filteredOptions);

      if (!fieldValue && response?.data?.length > 0) {
        fieldOnChange(filteredOptions[0].id);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    getInitialData({
      objective,
    });
    // eslint-disable-next-line
  }, [objective, integrationID, adAccountID]);

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={""}
        render={({ field }) => (
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              field.onChange(value?.id);
            }}
            noOptionsText={t(IDENTITIES_TIK_TOK_EMPTY)}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            disableClearable={true}
            isOptionEqualToValue={(option, value) => option?.id === value}
            getOptionLabel={(option) => {
              if (option == null || option === "") return "";
              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                return `${optionValue?.name}`;
              }
              if (typeof option === "string") {
                return option;
              }
              return option?.name || "";
            }}
            options={options}
            renderOption={(props, option) => (
              <Box
                {...props}
                component="li"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
                key={option.id || option.name}
              >
                <Avatar
                  src={option.imageURL}
                  alt={option.name}
                  sx={{
                    width: 30,
                    height: 30,
                  }}
                />
                <Typography variant="body1">{option.name}</Typography>
              </Box>
            )}
            filterOptions={(options, params) => filter(options, params)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                hiddenLabel={!label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      />
      <FieldCaption error={error} helperText={helperText} />
    </>
  );
};

export default SelectTikTokIdentityCustom;
